/**
 * CompoundPill — shared RS/Strategies strength + momentum indicator
 *
 * [Strong ↗]  Strength fills the pill; Momentum adds the directional arrow.
 *
 * Rules (from design system):
 *   - Direction never colour-alone: both the label AND the arrow are always visible.
 *   - Field register (muted): used here because this appears in dense surfaces
 *     (SidePanel sections, RS tables, Strategy rows).
 *   - Neutral → tan/surface-2, not green or red.
 */

import React from 'react';

// ── Strength → visual treatment (field register) ────────────────────────────
const STRENGTH_STYLE = {
  Strong: { bg: 'var(--up-field-bg)',   color: 'var(--up-field-ink)',   label: 'Strong' },
  Equal:  { bg: 'var(--surface-2)',     color: 'var(--ink-2)',          label: 'Equal'  },
  Weak:   { bg: 'var(--down-field-bg)', color: 'var(--down-field-ink)', label: 'Weak'   },
};

// ── Momentum → arrow glyph + color ──────────────────────────────────────────
const MOMENTUM_STYLE = {
  Accelerating:  { glyph: '↗', color: 'var(--up-field-ink)'  },
  Flat:          { glyph: '→', color: 'var(--ink-3)'          },
  Decelerating:  { glyph: '↘', color: 'var(--down-field-ink)' },
};

/**
 * @param {object} props
 * @param {'Strong'|'Equal'|'Weak'} props.strength
 * @param {'Accelerating'|'Flat'|'Decelerating'} props.momentum
 * @param {string} [props.className]
 */
export default function CompoundPill({ strength = 'Equal', momentum = 'Flat', className = '' }) {
  const s = STRENGTH_STYLE[strength] ?? STRENGTH_STYLE.Equal;
  const m = MOMENTUM_STYLE[momentum] ?? MOMENTUM_STYLE.Flat;

  return (
    <span
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0,
        borderRadius: 'var(--r-1)',
        border: '1px solid var(--hair-strong)',
        overflow: 'hidden',
        fontSize: 'var(--t-label)',
        fontFamily: 'var(--font-data)',
        fontWeight: 600,
        letterSpacing: 'var(--track-caps)',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
        userSelect: 'none',
        lineHeight: 1,
      }}
    >
      {/* Strength fill section */}
      <span style={{
        padding: '2px 6px',
        background: s.bg,
        color: s.color,
      }}>
        {s.label}
      </span>

      {/* Momentum arrow section */}
      <span style={{
        padding: '2px 5px',
        background: 'var(--surface-2)',
        color: m.color,
        borderLeft: '1px solid var(--hair-strong)',
        fontSize: 11,
        letterSpacing: 0,
      }}>
        {m.glyph}
      </span>
    </span>
  );
}
